import React from 'react';

/**
 * ErrorBoundary — a minimal, self-contained boundary that catches render/runtime errors in its
 * subtree and shows a friendly retry fallback instead of a white screen. Used to keep the QR guest
 * flow usable: a crash in CallStaff / order / chat / preview stays contained and recoverable.
 */
interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Log for diagnostics; the fallback keeps the page usable instead of white-screening.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  handleRetry = () => {
    // Clear the boundary so children re-mount and re-render from a clean state.
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={wrap}>
          <div style={card}>
            <div style={emoji} aria-hidden>😕</div>
            <div style={title}>Something went wrong.</div>
            <div style={sub}>Please tap to retry.</div>
            <button type="button" style={btn} onClick={this.handleRetry}>Try again</button>
            <button type="button" style={reloadBtn} onClick={() => window.location.reload()}>Reload page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const wrap: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, boxSizing: 'border-box' };
const card: React.CSSProperties = { width: 'min(440px, 100%)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 26, color: '#e5e7eb', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const emoji: React.CSSProperties = { fontSize: 40, lineHeight: 1 };
const title: React.CSSProperties = { fontSize: '1.1rem', fontWeight: 800 };
const sub: React.CSSProperties = { color: '#94a3b8', fontSize: '0.95rem', lineHeight: 1.4 };
const btn: React.CSSProperties = { marginTop: 8, background: 'linear-gradient(135deg,#f43f5e,#fb7185)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 22px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer' };
const reloadBtn: React.CSSProperties = { background: 'rgba(255,255,255,0.08)', color: '#e5e7eb', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '9px 16px', fontWeight: 700, cursor: 'pointer' };

export default ErrorBoundary;
